<template>
  <div class="redeem-gift-page">
    <div class="container">
      <div class="gift-icon-wrapper">
        <i class="fa-solid fa-gift gift-icon"></i>
      </div>
      <h1 class="title">Riscatta il tuo Regalo</h1>

      <div class="card">
        <!-- Loading State -->
        <div v-if="isLoading" class="alert alert-info">
          <i class="fa-solid fa-spinner fa-spin"></i>
          <div class="alert-content">
            <span>Verifica regalo in corso...</span>
          </div>
        </div>

        <!-- Error State -->
        <div v-if="error && !isLoading" class="alert alert-danger">
          <i class="fa-solid fa-triangle-exclamation"></i>
          <div class="alert-content">
            <div class="fw-bold">{{ error }}</div>
          </div>
        </div>

        <!-- Gift Not Found -->
        <div v-if="!isLoading && !gift && !error" class="alert alert-info">
          <i class="fa-solid fa-info-circle"></i>
          <div class="alert-content">
            <div class="fw-bold">Regalo non trovato</div>
            <div class="mt-2 text-sm">Il link potrebbe non essere valido.</div>
          </div>
        </div>

        <!-- Gift Found but Not Redeemable -->
        <div v-if="gift && !gift.canRedeem && !isLoading" class="alert alert-info">
          <i class="fa-solid fa-info-circle"></i>
          <div class="alert-content">
            <div class="fw-bold">{{ gift.message }}</div>
          </div>
        </div>

        <!-- Success State -->
        <div v-if="redeemResult && redeemResult.success" class="alert alert-success">
          <i class="fa-solid fa-circle-check"></i>
          <div class="alert-content">
            <div class="fw-bold">Regalo riscattato con successo!</div>
            <div class="mt-2">
              Hai ottenuto <strong>{{ redeemResult.days }} giorni</strong> di 
              <strong>{{ redeemResult.productName }}</strong> 
              ({{ redeemResult.variationName }})!
            </div>
            <div class="mt-3">
              <a href="/" class="btn btn-custom">Vai al tuo account</a>
            </div>
          </div>
        </div>

        <!-- Gift Details & Redeem Button -->
        <div v-if="gift && gift.canRedeem && !redeemResult && !isLoading">
          <div class="gift-details">
            <div class="gift-info">
              <i class="fa-solid fa-box-open"></i>
              <div>
                <div class="gift-label">Prodotto</div>
                <div class="gift-value">{{ gift.productName }}</div>
              </div>
            </div>
            <div class="gift-info">
              <i class="fa-solid fa-tags"></i>
              <div>
                <div class="gift-label">Variazione</div>
                <div class="gift-value">{{ gift.variationName }}</div>
              </div>
            </div>
            <div class="gift-info">
              <i class="fa-solid fa-calendar-days"></i>
              <div>
                <div class="gift-label">Durata</div>
                <div class="gift-value">{{ gift.days }} giorni</div>
              </div>
            </div>
            <div v-if="gift.expiresAt" class="gift-info">
              <i class="fa-solid fa-clock"></i>
              <div>
                <div class="gift-label">Scade il</div>
                <div class="gift-value">{{ formatDate(gift.expiresAt) }}</div>
              </div>
            </div>
          </div>

          <div v-if="!userToken" class="auth-required">
            <p class="text-sm mb-3">Per riscattare il regalo devi prima accedere al tuo account.</p>
            <button class="btn btn-custom w-100" @click="redirectToLogin">
              <i class="fa-solid fa-right-to-bracket me-2"></i>
              Accedi per riscattare
            </button>
            <p class="text-sm mt-2">
              Non hai un account? 
              <a href="#" @click.prevent="redirectToRegister" class="register-link">Registrati</a>
            </p>
          </div>

          <div v-else>
            <button
              class="btn btn-custom w-100"
              :disabled="isRedeeming"
              @click="redeemGift"
            >
              <span v-if="isRedeeming">
                <i class="fa-solid fa-spinner fa-spin me-1"></i>Riscatto in corso...
              </span>
              <span v-else>
                <i class="fa-solid fa-gift me-1"></i>Riscatta il Regalo
              </span>
            </button>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { API_CONFIG } from '@/config/api';

export default {
  name: 'RedeemMarketingGift',
  data() {
    return {
      giftToken: null,
      userToken: null,
      isLoading: false,
      isRedeeming: false,
      gift: null,
      redeemResult: null,
      error: null,
    };
  },
  mounted() {
    // Preleva i token dall'URL
    this.giftToken = this.$route.query?.giftToken;
    this.userToken = this.$route.query?.userToken;

    if (!this.giftToken) {
      this.error = 'Token regalo mancante';
      return;
    }

    // Verifica il regalo
    this.checkGift();
  },
  methods: {
    async checkGift() {
      this.isLoading = true;
      this.error = null;

      try {
        const apiUrl = `${API_CONFIG.BASE_URL}/api/marketing-gift/check/${this.giftToken}`;
        const res = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.message || 'Errore durante la verifica del regalo');
        }

        const data = await res.json();
        
        if (!data.found) {
          this.error = data.message || 'Regalo non trovato';
          return;
        }

        this.gift = data;
      } catch (e) {
        this.error = e.message || 'Errore durante la verifica del regalo';
      } finally {
        this.isLoading = false;
      }
    },

    async redeemGift() {
      if (!this.userToken) {
        this.redirectToLogin();
        return;
      }

      this.isRedeeming = true;
      this.error = null;

      try {
        const apiUrl = `${API_CONFIG.BASE_URL}/api/marketing-gift/redeem/${this.giftToken}`;
        const res = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.userToken}`,
          },
        });

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          
          // Se richiede autenticazione, reindirizza al login
          if (res.status === 401 || errorData.requiresAuth) {
            this.redirectToLogin();
            return;
          }
          
          throw new Error(errorData.message || 'Errore durante il riscatto');
        }

        const data = await res.json();
        
        if (data.success) {
          this.redeemResult = data;
        } else {
          this.error = data.message;
        }
      } catch (e) {
        this.error = e.message || 'Errore durante il riscatto';
      } finally {
        this.isRedeeming = false;
      }
    },

    redirectToLogin() {
      const returnUrl = encodeURIComponent(`/redeem-gift?giftToken=${this.giftToken}`);
      this.$router.push({ path: '/login', query: { returnUrl } });
    },

    redirectToRegister() {
      const returnUrl = encodeURIComponent(`/redeem-gift?giftToken=${this.giftToken}`);
      this.$router.push({ path: '/register', query: { returnUrl } });
    },

    formatDate(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      return date.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      });
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&display=swap');

.redeem-gift-page {
  font-family: 'Montserrat', sans-serif;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: #292D7D;
  color: white;
  padding: 2rem;
}

.container {
  max-width: 600px;
  width: 100%;
}

.gift-icon-wrapper {
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
}

.gift-icon {
  font-size: 4rem;
  color: #e83539;
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
}

.title {
  font-family: 'Montserrat', sans-serif;
  font-size: 2rem;
  font-weight: 700;
  color: white;
  margin-bottom: 2rem;
}

.card {
  background-color: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 0;
  padding: 2rem;
  margin-bottom: 2rem;
}

.gift-details {
  display: grid;
  gap: 1rem;
  margin-bottom: 2rem;
  text-align: left;
}

.gift-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.gift-info i {
  font-size: 1.5rem;
  color: #e83539;
  width: 2rem;
  text-align: center;
}

.gift-label {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.gift-value {
  font-size: 1.1rem;
  font-weight: 600;
  color: white;
}

.auth-required {
  text-align: center;
  padding: 1rem 0;
}

.register-link {
  color: #e83539;
  text-decoration: underline;
}

.btn {
  font-family: 'Montserrat', sans-serif;
  border-radius: 0;
  text-transform: uppercase;
  font-weight: 600;
  padding: 12px 24px;
  border: none;
  cursor: pointer;
  transition: background-color 0.3s ease;
  display: inline-block;
  text-decoration: none;
}

.btn-custom {
  background-color: #e83539;
  color: white;
  font-size: 1rem;
}

.btn-custom:hover:not(:disabled) {
  background-color: #cc2e31;
  color: white;
}

.btn-custom:disabled {
  background-color: #999;
  cursor: not-allowed;
}

.alert {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  margin-bottom: 1.5rem;
  border-radius: 0;
  border: 2px solid;
}

.alert-success {
  background-color: rgba(40, 167, 69, 0.2);
  border-color: #28a745;
  color: white;
}

.alert-danger {
  background-color: rgba(232, 53, 57, 0.2);
  border-color: #e83539;
  color: white;
}

.alert-info {
  background-color: rgba(23, 162, 184, 0.2);
  border-color: #17a2b8;
  color: white;
}

.alert-content {
  flex: 1;
  text-align: left;
}

.alert i:first-child {
  font-size: 1.5rem;
  margin-top: 0.2rem;
}

.w-100 {
  width: 100%;
}

.mt-2 {
  margin-top: 0.5rem;
}

.mt-3 {
  margin-top: 1rem;
}

.mb-3 {
  margin-bottom: 1rem;
}

.me-1 {
  margin-right: 0.25rem;
}

.me-2 {
  margin-right: 0.5rem;
}

.fw-bold {
  font-weight: 700;
}

.text-sm {
  font-size: 0.875rem;
}
</style>
